import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, GripVertical, HelpCircle, Triangle } from 'lucide-react';
import HelpDialog from '@/components/HelpDialog';
import ContextSelector from '@/components/ContextSelector';
import LinkedListPicker from '@/components/LinkedListPicker';
import { useSettings } from '@/contexts/SettingsContext';

const TYPE_TAGS_HELP = `A task's productivity type indicates **what kind of impact** that task will have on your life if you do or don't complete it:

- 🟢 <u>Must-do tasks</u> — There are **direct, negative consequences if you fail to complete these tasks on time**. Examples include paying rent, completing a class assignment, or feeding your child. Typically, these tasks are tied to responsibilities and commitments.
- 🟣 <u>Should-do tasks</u> — There aren't any direct, negative consequences if you don't complete these tasks; however, **completing them will have a significant positive effect**. In other words, the negative consequence of not doing them is an indirect consequence of missed opportunities. Examples include playing a musical instrument for fun, working on your fantasy novel, or planning a weekend camping trip with your friends.
- 🟠 <u>Could-do tasks</u> — There aren't any direct, negative consequences if you don't complete these tasks and **the positive effect of completing them is minor**. These aren't necessarily bad to work on, but they can easily be distractions. Examples include organizing your Spotify music library or cleaning out the attic.

It is very important to note that **which category a task falls into will differ from person to person**. For some people a certain task will be a "Must-do" while for someone else that same task might be a "Should-do".

Generally speaking and all things being equal, **Must-do tasks are higher priority than Should-do tasks, which are higher priority than Could-do tasks**. This is not a hard-and-fast rule, however, and discernment must be used with each task! These categories are only a guide to help you sort through your tasks and prioritize them well.`;

const DOMAIN_TAGS_HELP = `This tag category is pretty self explanatory: **different tasks belong to different spheres of life**, such as family, work, home, or finances. Sure, there is sometimes overlap, but you can usually tell pretty quickly which of these "domains" a task belongs to. Go with your gut an don't overthink it!

The important part is that these domains shouldn't all have the same priority in our lives: some should be higher in priority than others. **Reorder these tags to align with your personal values, with the more important domains at the top of the list and the less important domains at the bottom**. However, also recognize that this ordering does not create a hard-and-fast rule that you must follow 100% of the time! This is only meant to be a starting point to help you sort through and prioritize your tasks.`;

const ENERGY_LOAD_TAGS_HELP = `**Different tasks require different amounts of energy to complete.** Unfortunately, we often make the mistake of putting off harder tasks in favor of doing lots of smaller, easier tasks. This is because our brains are wired to avoid stress. However, this is bad because it is often the harder tasks that are actually higher in priority. And inevitably when we prioritize other tasks first, we end up running out of time and/or energy to complete the tasks which are more important. One solution is to simply **be aware that our brains try to avoid these higher energy tasks and then intentionally prioritize them higher on your to-do list**.

The three default energy load tags—Low, Medium, and High—are only meant to be relative labels, not hard-and-fast categories. However, here are some loose descriptions to help you get started:

- ⚡ <u>Low</u>  — Quick and/or easy
- ⚡⚡ <u>Medium</u> — Average length and difficulty; neither easy or challenging
- ⚡⚡⚡ <u>High</u> — Challenging and/or time consuming

One more note: there are **different *kinds* of energy** to be mindful of as well. When we hear the word "energy" we usually think of physical energy; however, mental and emotional energy also exist. Just because a task isn't physically draining doesn't mean it won't be draining in one of these other aspects. So when you select the energy load of a task, let whichever energy type that will take the greatest hit determine which tag you choose.`;

const ASSIGNEE_TAGS_HELP = `Want to team up with a friend or partner to get stuff done? Create a tag for each of you here, then give each task an Assignee to keep your lists separate and organized. Both of you will need to log into the same account to use this feature.`;

import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SortableTagItem({ tag, onEdit, onDelete, dragHandlePosition = 'right', onMoveToTop, onMoveToBottom, onMoveUp, onMoveDown, activeLists, onLinkChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tag.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-card text-card-foreground rounded-xl border border-[hsl(var(--border))] px-6 py-4 flex items-center gap-3 hover:shadow-md transition-shadow"
      data-testid="tag-item"
    >
      <div className="flex items-center gap-3 flex-1">
        {dragHandlePosition === 'left' && (
          <>
            {/* Desktop: Full drag handle with double arrows */}
            <div className="hidden md:flex flex-col items-center justify-center self-center gap-1.5">
              {onMoveToTop && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToTop(tag.id);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors no-hover"
                  title="Move to top"
                >
                  <div className="flex flex-col items-center">
                    <Triangle size={14} className="-mb-1.5" />
                    <Triangle size={14} />
                  </div>
                </button>
              )}
              <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex flex-col gap-0">
                <GripVertical size={20} className="text-muted-foreground -mb-2" />
                <GripVertical size={20} className="text-muted-foreground" />
              </div>
              {onMoveToBottom && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToBottom(tag.id);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Move to bottom"
                >
                  <div className="flex flex-col items-center" style={{ transform: 'rotate(180deg)' }}>
                    <Triangle size={14} className="-mb-1.5" />
                    <Triangle size={14} />
                  </div>
                </button>
              )}
            </div>
          </>
        )}
        <span className="text-2xl">{tag.emoji}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{tag.name}</p>
          {tag.category === 'Domain' && onLinkChange && (
            <LinkedListPicker
              currentListId={tag.active_list_id || null}
              activeLists={activeLists}
              onChange={(listId) => onLinkChange(tag, listId)}
              testIdPrefix={`tag-link-${tag.id}`}
              triggerClassName="text-xs text-muted-foreground hover:text-black hover:bg-[var(--color-secondary)] rounded-md pl-0 pr-2 py-0.5 transition-colors block w-full text-left whitespace-normal break-words"
            />
          )}
        </div>
      </div>
      <div className="md:flex md:flex-col gap-1 items-center self-center hidden">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(tag)}
          data-testid="edit-tag-button"
          title="Edit Tag"
        >
          <Pencil size={14} className="text-blue-500" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={() => onDelete(tag.id)}
          data-testid="delete-tag-button"
          title="Delete Tag"
        >
          <Trash2 size={14} />
        </Button>
      </div>
      
      {/* Mobile: Vertically stacked Edit/Delete and 2x2 arrow grid */}
      <div className="md:hidden flex items-center gap-3 self-center">
        {/* Edit/Delete buttons stacked */}
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onEdit(tag)}
            title="Edit Tag"
          >
            <Pencil size={14} className="text-blue-500" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => onDelete(tag.id)}
            title="Delete Tag"
          >
            <Trash2 size={14} />
          </Button>
        </div>
        {/* 2x2 Arrow grid: [▲▲][▲] top row, [▼▼][▼] bottom row */}
        <div className="flex items-center gap-1">
          {/* Left column: double up, double down */}
          <div className="flex flex-col gap-1">
            {onMoveToTop && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToTop(tag.id);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                title="Move to top"
              >
                <div className="flex flex-col items-center">
                  <Triangle size={14} className="-mb-1.5" />
                  <Triangle size={14} />
                </div>
              </button>
            )}
            {onMoveToBottom && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToBottom(tag.id);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                title="Move to bottom"
              >
                <div className="flex flex-col items-center" style={{ transform: 'rotate(180deg)' }}>
                  <Triangle size={14} className="-mb-1.5" />
                  <Triangle size={14} />
                </div>
              </button>
            )}
          </div>
          {/* Right column: single up, single down */}
          <div className="flex flex-col gap-1">
            {onMoveUp && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(tag.id);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                title="Move up"
              >
                <Triangle size={14} />
              </button>
            )}
            {onMoveDown && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(tag.id);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 no-hover"
                title="Move down"
              >
                <Triangle size={14} style={{ transform: 'rotate(180deg)' }} />
              </button>
            )}
          </div>
        </div>
      </div>
      {dragHandlePosition === 'right' && (
        <>
          {/* Desktop: Full drag handle with double arrows */}
          <div className="hidden md:flex flex-col items-center justify-center self-center gap-1.5">
            {onMoveToTop && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToTop(tag.id);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors no-hover"
                title="Move to top"
              >
                <div className="flex flex-col items-center">
                  <Triangle size={14} className="-mb-1.5" />
                  <Triangle size={14} />
                </div>
              </button>
            )}
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex flex-col gap-0">
              <GripVertical size={20} className="text-muted-foreground -mb-2" />
              <GripVertical size={20} className="text-muted-foreground" />
            </div>
            {onMoveToBottom && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToBottom(tag.id);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors no-hover"
                title="Move to bottom"
              >
                <div className="flex flex-col items-center" style={{ transform: 'rotate(180deg)' }}>
                  <Triangle size={14} className="-mb-1.5" />
                  <Triangle size={14} />
                </div>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function TagsPage({ tags, fetchTags, dragHandlePosition = 'right' }) {
  const { assigneeTagsEnabled, energyTagsEnabled, contextsEnabled, contexts, selectedContextId } = useSettings();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [category, setCategory] = useState('Type');
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [priority, setPriority] = useState(1);
  const [tagContextId, setTagContextId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [activeLists, setActiveLists] = useState([]);

  useEffect(() => {
    axios.get(`${API}/active_lists`, { withCredentials: true })
      .then(r => setActiveLists(r.data || []))
      .catch(() => setActiveLists([]));
  }, []);

  const handleTagLinkChange = async (tag, listId) => {
    try {
      await axios.patch(
        `${API}/tags/${tag.id}`,
        { active_list_id: listId == null ? '' : listId },
        { withCredentials: true }
      );
      toast.success(listId ? 'Tag linked' : 'Tag unlinked');
      fetchTags();
    } catch (error) {
      console.error('Error linking tag:', error);
      toast.error('Failed to update link');
    }
  };
  const emojiInputRef = useRef(null);

  const typeTags = tags.filter((t) => t.category === 'Type').sort((a, b) => a.priority - b.priority);
  const domainTags = tags.filter((t) => t.category === 'Domain').sort((a, b) => a.priority - b.priority);
  const energyTags = tags.filter((t) => t.category === 'Energy').sort((a, b) => a.priority - b.priority);
  const assigneeTags = tags.filter((t) => t.category === 'assignee').sort((a, b) => a.priority - b.priority);

  // Horizontal layout — paddings align the left-most and right-most columns
  // with the page title (mirrors StoragePage horizontal-groups behaviour).
  const groupsContainerRef = useRef(null);
  const [horizontalPadding, setHorizontalPadding] = useState('1rem');
  const [horizontalRightPadding, setHorizontalRightPadding] = useState('1rem');
  useEffect(() => {
    const compute = () => {
      const title = document.querySelector('[data-testid="tags-page"] h1');
      const container = groupsContainerRef.current;
      if (!title || !container) return;
      const titleRect = title.getBoundingClientRect();
      setHorizontalPadding(`${titleRect.left}px`);
      // Use container's clientWidth (the actually scrollable viewport), not
      // window.innerWidth, so vertical scrollbars / device variations don't
      // throw off the cap. Each column is 420 px on sm+, full-bleed on mobile.
      const containerWidth = container.clientWidth;
      const columnWidth = containerWidth >= 640 ? 294 : containerWidth - 80;
      const rightPadding = containerWidth - columnWidth - titleRect.left;
      setHorizontalRightPadding(`${Math.max(16, rightPadding)}px`);
    };
    const t = setTimeout(compute, 100);
    window.addEventListener('resize', compute);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', compute);
    };
  }, [tags]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 8,
      },
    })
  );

  const resetForm = () => {
    setCategory('Type');
    setName('');
    setEmoji('');
    setPriority(1);
    setTagContextId(selectedContextId || null);
    setEditingTag(null);
  };

  // Helper to refresh tags with proper context filtering
  const refreshTags = () => {
    if (contextsEnabled && selectedContextId) {
      fetchTags(selectedContextId);
    } else {
      fetchTags();
    }
  };

  const handleDragEnd = async (event, categoryTags) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = categoryTags.findIndex((t) => t.id === active.id);
    const newIndex = categoryTags.findIndex((t) => t.id === over.id);

    const reorderedTags = arrayMove(categoryTags, oldIndex, newIndex);

    // Update priorities for all tags in this category
    try {
      const updatePromises = reorderedTags.map((tag, index) =>
        axios.patch(`${API}/tags/${tag.id}`, {
          category: tag.category,
          name: tag.name,
          emoji: tag.emoji,
          priority: index + 1,
        }, { withCredentials: true })
      );

      await Promise.all(updatePromises);
      toast.success('Tag order updated');
      refreshTags();
    } catch (error) {
      console.error('Error reordering tags:', error);
      toast.error('Failed to update tag order');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !emoji.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      // Determine context_id based on category - Assignee tags are global (no context)
      const contextIdToSend = category === 'assignee' ? null : (contextsEnabled ? tagContextId : null);
      
      if (editingTag) {
        await axios.patch(`${API}/tags/${editingTag.id}`, {
          category,
          name: name.trim(),
          emoji: emoji.trim(),
          priority: parseInt(priority),
          context_id: contextIdToSend,
        }, { withCredentials: true });
        toast.success('Tag updated successfully');
      } else {
        // Auto-assign priority as next available in category
        const categoryTags = tags.filter(t => t.category === category);
        const nextPriority = categoryTags.length > 0 
          ? Math.max(...categoryTags.map(t => t.priority)) + 1 
          : 1;
        
        await axios.post(`${API}/tags`, {
          category,
          name: name.trim(),
          emoji: emoji.trim(),
          priority: nextPriority,
          context_id: contextIdToSend,
        }, { withCredentials: true });
        toast.success('Tag created successfully');
      }
      resetForm();
      setShowAddDialog(false);
      refreshTags();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error('Failed to save tag');
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setCategory(tag.category);
    setName(tag.name);
    setEmoji(tag.emoji);
    setPriority(tag.priority);
    setTagContextId(tag.context_id || null);
    setShowAddDialog(true);
  };

  const handleDelete = (tagId) => {
    setTagToDelete(tagId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!tagToDelete) return;

    try {
      await axios.delete(`${API}/tags/${tagToDelete}`, { withCredentials: true });
      toast.success('Tag deleted successfully');
      setShowDeleteDialog(false);
      setTagToDelete(null);
      refreshTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
      setShowDeleteDialog(false);
      setTagToDelete(null);
    }
  };

  const handleAddTagForCategory = (category) => {
    setCategory(category);
    setShowAddDialog(true);
  };

  const handleMoveToTop = async (tagId, categoryTags) => {
    try {
      const tagToMove = categoryTags.find(t => t.id === tagId);
      if (!tagToMove) return;

      const otherTags = categoryTags.filter(t => t.id !== tagId);
      const newOrder = [tagToMove, ...otherTags];

      // Update priorities for all tags in this category
      const updatePromises = newOrder.map((tag, index) =>
        axios.patch(`${API}/tags/${tag.id}`, {
          category: tag.category,
          name: tag.name,
          emoji: tag.emoji,
          priority: index + 1,
        }, { withCredentials: true })
      );

      await Promise.all(updatePromises);
      toast.success('Tag moved to top');
      refreshTags();
    } catch (error) {
      console.error('Error moving tag to top:', error);
      toast.error('Failed to move tag');
    }
  };

  const handleMoveToBottom = async (tagId, categoryTags) => {
    try {
      const tagToMove = categoryTags.find(t => t.id === tagId);
      if (!tagToMove) return;

      const otherTags = categoryTags.filter(t => t.id !== tagId);
      const newOrder = [...otherTags, tagToMove];

      // Update priorities for all tags in this category
      const updatePromises = newOrder.map((tag, index) =>
        axios.patch(`${API}/tags/${tag.id}`, {
          category: tag.category,
          name: tag.name,
          emoji: tag.emoji,
          priority: index + 1,
        }, { withCredentials: true })
      );

      await Promise.all(updatePromises);
      toast.success('Tag moved to bottom');
      refreshTags();
    } catch (error) {
      console.error('Error moving tag to bottom:', error);
      toast.error('Failed to move tag');
    }
  };

  const handleMoveUp = async (tagId, categoryTags) => {
    try {
      const currentIndex = categoryTags.findIndex(t => t.id === tagId);
      if (currentIndex <= 0) return; // Already at top or not found
      
      const newOrder = [...categoryTags];
      // Swap with tag above
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      
      // Update priorities for all tags in this category
      const updatePromises = newOrder.map((tag, index) =>
        axios.patch(`${API}/tags/${tag.id}`, {
          category: tag.category,
          name: tag.name,
          emoji: tag.emoji,
          priority: index + 1,
        }, { withCredentials: true })
      );
      
      await Promise.all(updatePromises);
      toast.success('Tag moved up');
      refreshTags();
    } catch (error) {
      console.error('Error moving tag up:', error);
      toast.error('Failed to move tag');
    }
  };

  const handleMoveDown = async (tagId, categoryTags) => {
    try {
      const currentIndex = categoryTags.findIndex(t => t.id === tagId);
      if (currentIndex === -1 || currentIndex >= categoryTags.length - 1) return; // Already at bottom or not found
      
      const newOrder = [...categoryTags];
      // Swap with tag below
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      
      // Update priorities for all tags in this category
      const updatePromises = newOrder.map((tag, index) =>
        axios.patch(`${API}/tags/${tag.id}`, {
          category: tag.category,
          name: tag.name,
          emoji: tag.emoji,
          priority: index + 1,
        }, { withCredentials: true })
      );
      
      await Promise.all(updatePromises);
      toast.success('Tag moved down');
      refreshTags();
    } catch (error) {
      console.error('Error moving tag down:', error);
      toast.error('Failed to move tag');
    }
  };

  const renderTagSection = (title, tagList) => {
    // Extract category from title (e.g., "Type Tags" -> "Type")
    const category = title.split(' ')[0];
    
    // Get help content based on category
    let helpContent = `Help content for ${title} will go here.`;
    if (category === 'Type') {
      helpContent = TYPE_TAGS_HELP;
    } else if (category === 'Domain') {
      helpContent = DOMAIN_TAGS_HELP;
    } else if (category === 'Energy') {
      helpContent = ENERGY_LOAD_TAGS_HELP;
    } else if (category === 'Assignee') {
      helpContent = ASSIGNEE_TAGS_HELP;
    }
    
    return (
      <section
        className="space-y-3 flex-shrink-0 w-[calc(100vw-3rem)] sm:w-[294px] sm:min-w-[294px] sm:max-w-[294px]"
        data-testid={`tags-section-${category.toLowerCase()}`}
      >
        <div>
          <h2 className="h-font text-lg font-semibold flex items-center gap-2">
            {title}
            <HelpDialog 
              title={`${title} Guide`}
              content={helpContent}
              size="default"
            />
          </h2>
          {category === 'Assignee' && contextsEnabled && (
            <p className="text-xs text-muted-foreground mt-1">
              Assignee tags are shared across all contexts.
            </p>
          )}
        </div>
        <div className="space-y-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => handleDragEnd(event, tagList)}
          >
            <SortableContext items={tagList.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-3">
                {tagList.map((tag) => (
                  <li key={tag.id}>
                    <SortableTagItem 
                      tag={tag} 
                      onEdit={handleEdit} 
                      onDelete={handleDelete} 
                      dragHandlePosition={dragHandlePosition}
                      onMoveToTop={(tagId) => handleMoveToTop(tagId, tagList)}
                      onMoveToBottom={(tagId) => handleMoveToBottom(tagId, tagList)}
                      onMoveUp={(tagId) => handleMoveUp(tagId, tagList)}
                      onMoveDown={(tagId) => handleMoveDown(tagId, tagList)}
                      activeLists={activeLists}
                      onLinkChange={handleTagLinkChange}
                    />
                  </li>
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      </section>
    );
  };

  return (
    <main className="pb-6 sm:pb-8" data-testid="tags-page">
      {/* Desktop gradient overlays - fade from content edges to window edges */}
      <div className="hidden md:block pointer-events-none">
        {/* Left gradient */}
        <div
          className="fixed left-0 top-[60px] bottom-0 z-30"
          style={{
            width: `calc((100vw - 48rem) / 2)`,
            background: 'linear-gradient(to right, rgb(15 23 42) 0%, transparent 100%)',
          }}
        />
        {/* Right gradient */}
        <div
          className="fixed right-0 top-[60px] bottom-0 z-30"
          style={{
            width: `calc((100vw - 48rem) / 2)`,
            background: 'linear-gradient(to left, rgb(15 23 42) 0%, transparent 100%)',
          }}
        />
      </div>
      <div className="sticky top-[60px] z-20 pt-2 md:pt-6 pb-4 mb-6 bg-slate-900">
        <header className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Desktop: Header row */}
          <div className="hidden md:block">
            <div className="flex items-center gap-2">
              <h1 className="h-font text-2xl md:text-3xl font-semibold text-white">Tag Management</h1>
              <HelpDialog 
                title="Tag Management Guide" 
                content={`**Task tags can be added, edited, reordered, or deleted on this page.** This is where the magic happens that controls how tasks are grouped and sorted throughout the app!
            A core concept of the UpNext system is that **some tasks should be higher in priority than others due to how necessary they are to complete and what sphere of life they belong to**. The individual tags within each of the three categories—Type, Domain, and Energy—are ordered with the higher priority tags at the top and the lower priority tags at the bottom. Throughout the app, tasks are first sorted by Type, then by Domain, and then by Energy. The end result is that your highest priority tasks will naturally "rise to the top" of your lists. 
            UpNext To-Do comes pre-loaded with a default set of tags; however, you are encouraged to create your own and rearrange any of them to align with your own personal values. **All tags can be reordered via drag-and-drop**, which also updates the priority order of all tasks in other lists (Task Storage and the Active List).`}
                size="large"
              />
            </div>
            
            {/* Action buttons row - below header, left-aligned */}
            <div className="mt-3 flex items-center gap-2">
              <ContextSelector
                showEditButton={true}
                triggerClassName="h-8 bg-transparent border border-[var(--color-secondary)] text-[var(--color-secondary)] hover:bg-[var(--color-secondary)] hover:text-black"
                align="start"
              />
              <Dialog open={showAddDialog} onOpenChange={(open) => {
                setShowAddDialog(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button 
                    variant="secondary"
                    size="sm"
                    data-testid="add-tag-button"
                    className="gap-2"
                    style={{
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      backgroundColor: 'var(--color-secondary)'
                    }}
                  >
                    <Plus size={16} />
                    Add Tag
                  </Button>
                </DialogTrigger>
          <DialogContent data-testid="add-tag-dialog">
            <DialogHeader>
              <DialogTitle className="h-font text-xl">
                {editingTag ? 'Edit Tag' : 'Add New Tag'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Emoji and Name row - labels and inputs aligned */}
              <div className="flex gap-3">
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <Label htmlFor="tag-emoji">Emoji</Label>
                  <input
                    ref={emojiInputRef}
                    id="tag-emoji"
                    type="text"
                    value={emoji}
                    onChange={(e) => setEmoji(e.target.value)}
                    className="w-14 h-9 text-xl text-center border border-[hsl(var(--border))] rounded-md bg-card hover:bg-[hsl(var(--secondary))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] transition-colors"
                    placeholder="🙂"
                    maxLength={4}
                    data-testid="emoji-input-field"
                  />
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <Label htmlFor="tag-name">Name</Label>
                  <Input
                    id="tag-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Work"
                    data-testid="tag-name-input"
                    autoFocus
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Click the emoji field and type [Win + .] on Windows or<br />[Cmd + Ctrl + Space] on Mac to select an emoji.
              </p>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="tag-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assigneeTagsEnabled && <SelectItem value="assignee">Assignee</SelectItem>}
                    <SelectItem value="Type">Type</SelectItem>
                    <SelectItem value="Domain">Domain</SelectItem>
                    {energyTagsEnabled && <SelectItem value="Energy">Energy</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {/* Context selector - only show when contexts enabled and not Assignee category */}
              {contextsEnabled && category !== 'assignee' && contexts.length > 0 && (
                <div className="space-y-2">
                  <Label>Context</Label>
                  <Select value={tagContextId || ''} onValueChange={(val) => setTagContextId(val || null)}>
                    <SelectTrigger data-testid="tag-context-select">
                      <SelectValue placeholder="Select context..." />
                    </SelectTrigger>
                    <SelectContent>
                      {contexts.map((ctx) => (
                        <SelectItem key={ctx.id} value={ctx.id}>
                          {ctx.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Type, Domain, and Energy tags belong to a specific context. Switching the selected Context also switches the tags.
                  </p>
                </div>
              )}
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  data-testid="save-tag-button"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                >
                  {editingTag ? 'Save Changes' : 'Create Tag'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          </div>
          </div>
          
          {/* Mobile: Header with Add Tag button */}
          <div className="md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h1 className="h-font text-2xl font-semibold text-white">Tag Management</h1>
                <HelpDialog 
                  title="Tag Management Guide" 
                  content={`**Task tags can be added, edited, reordered, or deleted on this page.** This is where the magic happens that controls how tasks are grouped and sorted throughout the app!
            A core concept of the UpNext system is that **some tasks should be higher in priority than others due to how necessary they are to complete and what sphere of life they belong to**. The individual tags within each of the three categories—Type, Domain, and Energy—are ordered with the higher priority tags at the top and the lower priority tags at the bottom. Throughout the app, tasks are first sorted by Type, then by Domain, and then by Energy. The end result is that your highest priority tasks will naturally "rise to the top" of your lists. 
            UpNext To-Do comes pre-loaded with a default set of tags; however, you are encouraged to create your own and rearrange any of them to align with your own personal values. **All tags can be reordered via drag-and-drop**, which also updates the priority order of all tasks in other lists (Task Storage and the Active List).`}
                  size="large"
                />
              </div>
              <div className="flex items-center gap-2">
                <ContextSelector
                  showEditButton={true}
                  triggerClassName="h-8 bg-transparent border border-[var(--color-secondary)] text-[var(--color-secondary)] hover:bg-[var(--color-secondary)] hover:text-black"
                  align="end"
                />
                <Dialog open={showAddDialog} onOpenChange={(open) => {
                  setShowAddDialog(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="secondary"
                      size="sm"
                      data-testid="add-tag-button-mobile"
                      className="gap-2"
                      style={{
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        backgroundColor: 'var(--color-secondary)'
                      }}
                    >
                      <Plus size={16} />
                      Add Tag
                    </Button>
                  </DialogTrigger>
                </Dialog>
              </div>
            </div>
          </div>
        </header>
      </div>

      <div
        ref={groupsContainerRef}
        className="storage-horizontal-scroll flex gap-4 sm:gap-6 overflow-x-auto pb-4"
        style={{
          paddingLeft: horizontalPadding,
          paddingRight: horizontalRightPadding,
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="tags-groups-container"
      >
        {assigneeTagsEnabled && renderTagSection('Assignee Tags', assigneeTags)}
        {renderTagSection('Type Tags', typeTags)}
        {renderTagSection('Domain Tags', domainTags)}
        {energyTagsEnabled && renderTagSection('Energy Tags', energyTags)}
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6">

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Tag</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Are you sure? This action cannot be undone.
            </p>
            <DialogFooter className="gap-3 sm:space-x-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setTagToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete}
                data-testid="delete-tag-confirm-button"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
